import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  getObjectId,
  ServiceEvent,
  ServiceEventCreateDTO,
  serviceEventSelector,
  useGetServiceEventQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  ServiceEventForm,
  ServiceEventFormDefaultValues,
  ServiceEventFormValues
} from "../../components/form/ServiceEventForm/ServiceEventForm";
import {useServiceEventMutation} from "../../hooks/useServiceEventMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {toFormDefaultsFromServiceEvent} from "../../components/form/ServiceEventForm/useServiceEventForm";
import {
  ServiceEventEditForm,
  ServiceEventEditFormValues
} from "../../components/form/ServiceEventForm/ServiceEventEditForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: ServiceEventFormDefaultValues
}

export const ServiceEventFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <ServiceEventFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const ServiceEventFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId, googleMapsApiKey} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectServiceEvent = useMemo(() => {
    return serviceEventSelector()
  }, [])

  const {currServiceEvent, isLoading} = useGetServiceEventQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currServiceEvent: selectServiceEvent(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: ServiceEventFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromServiceEvent(currServiceEvent);
  }, [currServiceEvent, formDefaults]);

  const isEdit = useMemo(() => {
    return currServiceEvent && !copy
  }, [currServiceEvent, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.serviceevent.form.title.edit", "Edit Service Event", {ns: "common"});
    } else {
      return t("page.entity.serviceevent.form.title.new", "New Service Event", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    activateServiceEvent,
    updateServiceEvent,
    isCreating,
    isUpdating,
  } = useServiceEventMutation()

  const onServiceEventSubmit = useCallback((formValues: ServiceEventFormValues) => {
    if (!isEdit) {
      const serviceEvent: Partial<ServiceEventCreateDTO> = {
        eventType: formValues.eventType.code,
        companyId: getObjectId(formValues.company),
        name: formValues.name,
        severityLevel: formValues.severityLevel,
        startDttm: adjustToTimeZone(formValues.startDate).getTime(),
        endDttm: adjustToTimeZoneSafely(formValues.endDate)?.getTime(),
        note: formValues.note,
        regionId: regionId,
        targets: formValues.targets?.map((t) => ({
          objectId: t.objectId,
          objectType: t.objectType,
          targetMode: t.mode.code,
        })),
        footprintGeom: formValues.footprintGeom,
      };
      activateServiceEvent(serviceEvent, {onSuccess: () => navigate(-1)})
    }
  }, [isEdit, activateServiceEvent, navigate, regionId]);

  const onServiceEventEditSubmit = useCallback((formValues: ServiceEventEditFormValues) => {
    if (isEdit) {
      const serviceEvent: Partial<ServiceEvent> = {
        ...currServiceEvent.asTypeValue(),
        name: formValues.name,
        severityLevel: formValues.severityLevel,
        startDttm: adjustToTimeZone(formValues.startDate).getTime(),
        endDttm: adjustToTimeZoneSafely(formValues.endDate)?.getTime(),
        note: formValues.note,
      };
      updateServiceEvent(serviceEvent, {onSuccess: () => navigate(-1)})
    }
  }, [currServiceEvent, isEdit, updateServiceEvent, navigate]);

  return (
    <ProtectedPage
      entity={currServiceEvent}
      permissions={[isEdit ? "serviceevent_edit" : "serviceevent_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                {isEdit ? (
                  <ServiceEventEditForm
                    defaultValues={_formDefaults}
                    submitting={isUpdating}
                    onSubmit={onServiceEventEditSubmit}/>
                ) : (
                  <ServiceEventForm
                    options={{
                      regionId: regionId,
                      googleMapsApiKey: googleMapsApiKey,
                    }}
                    defaultValues={_formDefaults}
                    submitting={isCreating}
                    onSubmit={onServiceEventSubmit}/>
                )}
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

